import { useEffect, useMemo, useState, type CSSProperties, type Dispatch, type SetStateAction } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import DataTable from '@/components/DataTable';
import { DropZone } from '@/components/UI/FileDropzone';
import { Button } from '@/components/UI/Button';
import { Text } from '@/components/UI/Typography/Text';
import useResize from '@/hooks/useResize';
import { theme } from '@/theme/Theme';
import type {
  PblAppointmentProposal,
  PblCaseLawyerSummary,
  PblDocument,
  PblMeeting,
} from '@/types/pbl';
import { getPblMeetingEditButtonLabel, getPblMeetingPurposeLabel } from '@/utils/pblMeeting';
import { translatePblDisplayValue } from '@/utils/pblDisplayTranslation';

import {
  formatDateTime,
  formatFileSize,
  formatMeetingDate,
  canEditMeetingTime,
  getDocumentFileName,
  getDocumentFileType,
  getDocumentMetadataText,
  getDocumentUploaderName,
  getMeetingBadgeKey,
  getMeetingName,
  isMeetingCancelled,
} from './helpers';
import {
  AppointmentReasonField,
  AppointmentReasonTextarea,
  BodyText,
  BulletList,
  DocumentUploadArea,
  DocumentTableScroll,
  FileIcon,
  FileMeta,
  FileName,
  FileNameText,
  FooterAction,
  FooterTextButton,
  InfoGrid,
  InfoLabel,
  InfoValue,
  LinkRow,
  MeetingDataTable,
  MeetingEndedBadge,
  MeetingNameRow,
  MobileEmptyRecord,
  MobilePageButton,
  MobilePageButtons,
  MobilePagination,
  MobilePaginationDisplay,
  MobileRecord,
  MobileRecordLabel,
  MobileRecords,
  MobileRecordValue,
  MobileSearchInput,
  Panel,
  SectionTitle,
  SignButton,
  SignColumnTitle,
  SignGrid,
  SignInfoGrid,
  SignatureBox,
  SignatureGrid,
  SignedBadge,
  TableActions,
  TableScroll,
  TextLink,
  TypeText,
  VerifyLink,
} from './styles';

export type CaseProgressSummary = {
  caseId: string;
  officeName: string;
  assistingStaffName: string;
  officeContact: string;
  enquirer: {
    fullName: string;
    identityPartial: string;
  };
};

type DocumentHandler = (document: PblDocument) => void;
type AsyncDocumentHandler = (document: PblDocument) => Promise<void>;
type MeetingHandler = (meeting: PblMeeting) => void | Promise<void>;
type DocumentTableRow = { id: string; document: PblDocument };

const meetingButtonStyle: CSSProperties = { fontSize: 17, whiteSpace: 'nowrap', width: 'auto' };
const signableDocumentExtensions = new Set(['pdf', 'doc', 'docx']);

const canJoinMeeting = (meeting: PblMeeting) => {
  if (isMeetingCancelled(meeting)) return false;
  if (typeof meeting.can_join === 'boolean') return meeting.can_join;

  return Boolean(meeting.meeting_url || meeting.vc_id);
};

const RenderMeetingName = ({ meeting, showEndedBadge = false }: { meeting: PblMeeting; showEndedBadge?: boolean }) => (
  <MeetingNameRow>
    <TranslatedMeetingName meeting={meeting} showEndedBadge={showEndedBadge} />
  </MeetingNameRow>
);

const TranslatedMeetingName = ({
  meeting,
  showEndedBadge = false,
}: {
  meeting: PblMeeting;
  showEndedBadge?: boolean;
}) => {
  //   @ts-ignore
  const { t } = useTranslation();
  const badgeKey = getMeetingBadgeKey(meeting, showEndedBadge);

  return (
    <>
      <span>{getMeetingName(meeting)}</span>
      {badgeKey && <MeetingEndedBadge>{t(`pbl.caseDetailDocuments.meeting.badges.${badgeKey}`)}</MeetingEndedBadge>}
    </>
  );
};
const canInviteDocumentToSign = (document: PblDocument) => {
  const extension = document.storage_file?.extension?.trim().toLowerCase().replace(/^\./, '');

  return !document.requires_signature && Boolean(extension && signableDocumentExtensions.has(extension));
};

const RenderDocumentName = ({
  document,
  canInspectDocument,
  loadingDocumentId,
  onInspectDocument,
}: {
  document: PblDocument;
  canInspectDocument: boolean;
  loadingDocumentId: number | null;
  onInspectDocument: AsyncDocumentHandler;
}) => {
  //   @ts-ignore
  const { t } = useTranslation();
  const fileName = getDocumentFileName(document);

  return (
    <FileName>
      <FileIcon aria-hidden="true" />
      <FileNameText className="pbl-document-file-name-text" title={fileName}>
        {fileName}
      </FileNameText>
      <FileMeta title={document.title}>{document.title}</FileMeta>
      {canInspectDocument && document.id && (
        <VerifyLink
          href="#verify"
          onClick={(event) => {
            event.preventDefault();
            onInspectDocument(document);
          }}
        >
          {loadingDocumentId === document.id
            ? t('pbl.caseDetailDocuments.documentsUpload.actions.loading')
            : t('pbl.caseDetailDocuments.documentsUpload.actions.verifyFile')}
        </VerifyLink>
      )}
    </FileName>
  );
};

export const CaseInfoPanel = ({ item }: { item: CaseProgressSummary }) => {
  //   @ts-ignore
  const { t } = useTranslation();

  return (
    <Panel>
      <SectionTitle>{t('pbl.caseDetailDocuments.caseInfo.districtOfficeStaff')}</SectionTitle>
      <InfoGrid>
        <InfoLabel>{t('pbl.caseDetailDocuments.caseInfo.fields.legcoOffice')}</InfoLabel>
        <InfoValue>{item.officeName}</InfoValue>
        <InfoLabel>{t('pbl.caseDetailDocuments.caseInfo.fields.assistingStaffName')}</InfoLabel>
        <InfoValue>{item.assistingStaffName}</InfoValue>
        <InfoLabel>{t('pbl.caseDetailDocuments.caseInfo.fields.officeContact')}</InfoLabel>
        <InfoValue>{item.officeContact}</InfoValue>
      </InfoGrid>

      <SectionTitle>{t('pbl.caseDetailDocuments.caseInfo.caseInformation')}</SectionTitle>
      <InfoGrid>
        <InfoLabel>{t('pbl.caseDetailDocuments.caseInfo.fields.caseId')}</InfoLabel>
        <InfoValue>{item.caseId}</InfoValue>
        <InfoLabel>{t('pbl.caseDetailDocuments.caseInfo.fields.enquirer')}</InfoLabel>
        <InfoValue>{item.enquirer.fullName}</InfoValue>
        <InfoLabel>{t('pbl.caseDetailDocuments.caseInfo.fields.identityPartial')}</InfoLabel>
        <InfoValue>{item.enquirer.identityPartial}</InfoValue>
      </InfoGrid>
    </Panel>
  );
};

export const getLawyerSummaryDisplayName = (summary?: PblCaseLawyerSummary | null) =>
  summary?.name || [summary?.name_en, summary?.name_zh].filter(Boolean).join(' / ');

export const getLawyerSummaryFirmName = (summary?: PblCaseLawyerSummary | null) =>
  [summary?.law_firm_name, summary?.law_firm_name_zh].filter(Boolean).join(' / ');

export const getLawyerSummaryPosition = (summary?: PblCaseLawyerSummary | null) =>
  summary?.position_in_law_firm || summary?.position || '';

export const LawyerSummaryPanel = ({
  summary,
  isLoadingCv,
  onViewCv,
}: {
  summary?: PblCaseLawyerSummary | null;
  isLoadingCv: boolean;
  onViewCv: () => void;
}) => {
  //   @ts-ignore
  const { t } = useTranslation();

  const position = translatePblDisplayValue(t, getLawyerSummaryPosition(summary));

  return (
    <Panel>
      <SectionTitle>{t('pbl.caseDetailDocuments.lawyerInfo.title')}</SectionTitle>
      <InfoGrid>
        <InfoLabel>{t('pbl.caseDetailDocuments.lawyerInfo.name')}</InfoLabel>
        <InfoValue>{getLawyerSummaryDisplayName(summary) || '-'}</InfoValue>
        <InfoLabel>{t('pbl.caseDetailDocuments.lawyerInfo.lawFirm')}</InfoLabel>
        <InfoValue>{getLawyerSummaryFirmName(summary) || '-'}</InfoValue>
        {position ? (
          <>
            <InfoLabel>{t('pbl.caseDetailDocuments.lawyerInfo.position')}</InfoLabel>
            <InfoValue>{position}</InfoValue>
          </>
        ) : null}
      </InfoGrid>
      {summary ? (
        <Button type="button" size="md" variant="primary" rounded={false} disabled={isLoadingCv} onClick={onViewCv}>
          {isLoadingCv ? t('pbl.caseDetailDocuments.lawyerInfo.loadingCv') : t('pbl.caseDetailDocuments.lawyerInfo.viewCv')}
        </Button>
      ) : null}
    </Panel>
  );
};

export const CaseApplicationPanel = ({
  archived = false,
  canViewRatingHistory,
  isDownloadingAuthorization = false,
  isDownloadingCase = false,
  onDownloadAuthorizationForm,
  onDownloadPersonalDetailsCase,
}: {
  archived?: boolean;
  canViewRatingHistory: boolean;
  isDownloadingAuthorization?: boolean;
  isDownloadingCase?: boolean;
  onDownloadAuthorizationForm?: () => void;
  onDownloadPersonalDetailsCase?: () => void;
}) => {
  //   @ts-ignore
  const { t } = useTranslation();

  if (archived) {
    return (
      <Panel>
        <SectionTitle>{t('pbl.caseDetailDocuments.caseApplication.title')}</SectionTitle>
        <BodyText>{t('pbl.caseDetailDocuments.caseApplication.archivedBody')}</BodyText>
      </Panel>
    );
  }

  return (
    <Panel>
      <SectionTitle>{t('pbl.caseDetailDocuments.caseApplication.title')}</SectionTitle>
      <BodyText>{t('pbl.caseDetailDocuments.caseApplication.body')}</BodyText>
      <LinkRow>
        <TextLink
          href="#authorization"
          onClick={(event) => {
            event.preventDefault();
            onDownloadAuthorizationForm?.();
          }}
        >
          {isDownloadingAuthorization
            ? t('pbl.caseDetailDocuments.caseApplication.links.downloading')
            : t('pbl.caseDetailDocuments.caseApplication.links.downloadAuthorizationForm')}
        </TextLink>
        <TextLink
          href="#case-detail"
          onClick={(event) => {
            event.preventDefault();
            onDownloadPersonalDetailsCase?.();
          }}
        >
          {isDownloadingCase
            ? t('pbl.caseDetailDocuments.caseApplication.links.downloading')
            : t('pbl.caseDetailDocuments.caseApplication.links.downloadCaseDetail')}
        </TextLink>
        {canViewRatingHistory && (
          <TextLink href="#rating-history">{t('pbl.caseDetailDocuments.caseApplication.links.ratingHistory')}</TextLink>
        )}
      </LinkRow>
    </Panel>
  );
};

export const MeetingPanel = ({
  meetings,
  canManageCase,
  canEditMeeting = false,
  joiningMeetingId,
  onJoinMeeting,
  onEditMeeting,
}: {
  meetings: PblMeeting[];
  canManageCase: boolean;
  canEditMeeting?: boolean;
  joiningMeetingId: number | null;
  onJoinMeeting: MeetingHandler;
  onEditMeeting?: MeetingHandler;
}) => {
  //   @ts-ignore
  const { t } = useTranslation();
  const { isMobile } = useResize();

  return (
  <Panel>
    <SectionTitle>{t('pbl.caseDetailDocuments.meeting.title')}</SectionTitle>
    <BodyText>{t('pbl.caseDetailDocuments.meeting.body')}</BodyText>
    {isMobile ? (
      meetings.length > 0 ? (
        <MobileRecords aria-label={t('pbl.caseDetailDocuments.meeting.table.ariaLabel')}>
          {meetings.map((meeting) => {
            const purposeLabel = getPblMeetingPurposeLabel(meeting.purpose, t);
            const canJoinThisMeeting = canJoinMeeting(meeting);
            const canEditThisMeeting = Boolean(canEditMeeting && onEditMeeting && canEditMeetingTime(meeting));
            const badgeKey = getMeetingBadgeKey(meeting, true);

            return (
              <MobileRecord key={meeting.id}>
                <MobileRecordLabel>{t('pbl.caseDetailDocuments.meeting.table.conferenceName')}</MobileRecordLabel>
                <MobileRecordValue>
                  <TranslatedMeetingName meeting={meeting} />
                  {purposeLabel && <FileMeta>{purposeLabel}</FileMeta>}
                </MobileRecordValue>
                <MobileRecordLabel>{t('pbl.caseDetailDocuments.meeting.table.date')}</MobileRecordLabel>
                <MobileRecordValue style={{ whiteSpace: 'pre-line' }}>{formatMeetingDate(meeting)}</MobileRecordValue>
                <MobileRecordLabel>{t('pbl.caseDetailDocuments.meeting.table.action')}</MobileRecordLabel>
                <MobileRecordValue>
                  {canManageCase && canJoinThisMeeting ? (
                    <TableActions>
                      <Button
                        variant="primary"
                        type="button"
                        disabled={joiningMeetingId === meeting.id}
                        onClick={() => onJoinMeeting(meeting)}
                      >
                        {joiningMeetingId === meeting.id
                          ? t('pbl.caseDetailDocuments.meeting.actions.joining')
                          : t('pbl.caseDetailDocuments.meeting.actions.joinMeeting')}
                      </Button>
                      {canEditThisMeeting && (
                        <Button variant="primary-invert" type="button" onClick={() => onEditMeeting?.(meeting)}>
                          {getPblMeetingEditButtonLabel(meeting.purpose, t)}
                        </Button>
                      )}
                    </TableActions>
                  ) : canManageCase && canEditThisMeeting ? (
                    <TableActions>
                      <Button variant="primary-invert" type="button" onClick={() => onEditMeeting?.(meeting)}>
                        {getPblMeetingEditButtonLabel(meeting.purpose, t)}
                      </Button>
                    </TableActions>
                  ) : badgeKey ? (
                    <MeetingEndedBadge>{t(`pbl.caseDetailDocuments.meeting.badges.${badgeKey}`)}</MeetingEndedBadge>
                  ) : (
                    '-'
                  )}
                </MobileRecordValue>
              </MobileRecord>
            );
          })}
        </MobileRecords>
      ) : (
        <MobileEmptyRecord>{t('pbl.caseDetailDocuments.meeting.table.empty')}</MobileEmptyRecord>
      )
    ) : (
      <TableScroll>
      <MeetingDataTable aria-label={t('pbl.caseDetailDocuments.meeting.table.ariaLabel')}>
        <colgroup>
          <col data-testid="meeting-column" style={{ width: '35%' }} />
          <col data-testid="meeting-column" style={{ width: '35%' }} />
          <col data-testid="meeting-column" />
        </colgroup>
        <thead>
          <tr>
            <th>{t('pbl.caseDetailDocuments.meeting.table.conferenceName')}</th>
            <th>{t('pbl.caseDetailDocuments.meeting.table.date')}</th>
            <th>{t('pbl.caseDetailDocuments.meeting.table.action')}</th>
          </tr>
        </thead>
        <tbody>
          {meetings.length > 0 ? (
            meetings.map((meeting) => {
              const purposeLabel = getPblMeetingPurposeLabel(meeting.purpose, t);
              const canJoinThisMeeting = canJoinMeeting(meeting);
              const canEditThisMeeting = Boolean(canEditMeeting && onEditMeeting && canEditMeetingTime(meeting));

              return (
                <tr key={meeting.id}>
                  <td>
                    <RenderMeetingName meeting={meeting} showEndedBadge />
                    {purposeLabel && <FileMeta>{purposeLabel}</FileMeta>}
                  </td>
                  <td style={{ whiteSpace: 'pre-line' }}>{formatMeetingDate(meeting)}</td>
                  <td>
                    {canManageCase && canJoinThisMeeting && (
                      <TableActions>
                        <Button
                          variant="primary"
                          type="button"
                          style={meetingButtonStyle}
                          disabled={joiningMeetingId === meeting.id}
                          onClick={() => onJoinMeeting(meeting)}
                        >
                          {joiningMeetingId === meeting.id
                            ? t('pbl.caseDetailDocuments.meeting.actions.joining')
                            : t('pbl.caseDetailDocuments.meeting.actions.joinMeeting')}
                        </Button>
                        {canEditThisMeeting && (
                          <Button
                            variant="primary-invert"
                            type="button"
                            style={meetingButtonStyle}
                            onClick={() => onEditMeeting?.(meeting)}
                          >
                            {getPblMeetingEditButtonLabel(meeting.purpose, t)}
                          </Button>
                        )}
                      </TableActions>
                    )}
                    {canManageCase && !canJoinThisMeeting && canEditThisMeeting && (
                      <TableActions>
                        <Button
                          variant="primary-invert"
                          type="button"
                          style={meetingButtonStyle}
                          onClick={() => onEditMeeting?.(meeting)}
                        >
                          {getPblMeetingEditButtonLabel(meeting.purpose, t)}
                        </Button>
                      </TableActions>
                    )}
                  </td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan={3}>{t('pbl.caseDetailDocuments.meeting.table.empty')}</td>
            </tr>
          )}
        </tbody>
      </MeetingDataTable>
      </TableScroll>
    )}

  </Panel>
  );
};

export const AppointmentProposalPanel = ({
  proposal,
  canRespond,
  canEdit = false,
  respondingActionKey,
  onSelectOption,
  onDeclineAll,
  onEditProposal,
}: {
  proposal: PblAppointmentProposal;
  canRespond: boolean;
  canEdit?: boolean;
  respondingActionKey: string | null;
  onSelectOption: (proposal: PblAppointmentProposal, optionIndex: number) => void;
  onDeclineAll: (proposal: PblAppointmentProposal, unavailableReason: string) => void;
  onEditProposal?: (proposal: PblAppointmentProposal) => void;
}) => {
  //   @ts-ignore
  const { t } = useTranslation();
  const [unavailableReason, setUnavailableReason] = useState('');

  return (
    <Panel>
      <SectionTitle>{t('pbl.caseDetailDocuments.appointmentProposal.title')}</SectionTitle>
      <BodyText>
        {canRespond
          ? t('pbl.caseDetailDocuments.appointmentProposal.respondBody')
          : t('pbl.caseDetailDocuments.appointmentProposal.waitingBody')}
      </BodyText>
      {canEdit && onEditProposal && (
        <FooterAction style={{ marginTop: 0, marginBottom: 12 }}>
          <span />
          <Button variant="primary-invert" type="button" onClick={() => onEditProposal(proposal)}>
            {t('pbl.caseDetailDocuments.appointmentProposal.edit')}
          </Button>
        </FooterAction>
      )}
      <TableScroll>
        <MeetingDataTable aria-label={t('pbl.caseDetailDocuments.appointmentProposal.table.ariaLabel')}>
          <colgroup>
            <col style={{ width: '16%' }} />
            <col style={{ width: '54%' }} />
            <col style={{ width: '30%' }} />
          </colgroup>
          <thead>
            <tr>
              <th>{t('pbl.caseDetailDocuments.appointmentProposal.table.option')}</th>
              <th>{t('pbl.caseDetailDocuments.appointmentProposal.table.proposedTime')}</th>
              <th>{t('pbl.caseDetailDocuments.appointmentProposal.table.action')}</th>
            </tr>
          </thead>
          <tbody>
            {proposal.options.length > 0 ? (
              proposal.options.map((option, index) => {
                const actionKey = `select-${proposal.id}-${index}`;

                return (
                  <tr key={`${proposal.id}-${option.starts_at}-${index}`}>
                    <td>{t('pbl.caseDetailDocuments.appointmentProposal.table.optionNumber', { number: index + 1 })}</td>
                    <td>
                      <div>{`${formatDateTime(option.starts_at)} - ${formatDateTime(option.ends_at)}`}</div>
                      {option.note && <FileMeta>{option.note}</FileMeta>}
                    </td>
                    <td>
                      {canRespond && (
                        <TableActions>
                          <Button
                            variant="primary"
                            type="button"
                            disabled={Boolean(respondingActionKey)}
                            onClick={() => onSelectOption(proposal, index)}
                          >
                            {respondingActionKey === actionKey
                              ? t('pbl.caseDetailDocuments.appointmentProposal.actions.selecting')
                              : t('pbl.caseDetailDocuments.appointmentProposal.actions.selectOption', {
                                  number: index + 1,
                                })}
                          </Button>
                        </TableActions>
                      )}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={3}>{t('pbl.caseDetailDocuments.appointmentProposal.table.empty')}</td>
              </tr>
            )}
          </tbody>
        </MeetingDataTable>
      </TableScroll>
      {canRespond && proposal.options.length > 0 && (
        <>
          <AppointmentReasonField>
            {t('pbl.caseDetailDocuments.appointmentProposal.availableMeetingTimes')}
            <AppointmentReasonTextarea
              aria-label={t('pbl.caseDetailDocuments.appointmentProposal.availableMeetingTimes')}
              value={unavailableReason}
              placeholder={t('pbl.caseDetailDocuments.appointmentProposal.availableMeetingTimesPlaceholder')}
              disabled={Boolean(respondingActionKey)}
              onChange={(event) => setUnavailableReason(event.target.value)}
            />
          </AppointmentReasonField>
          <FooterAction>
            <FooterTextButton
              type="button"
              disabled={Boolean(respondingActionKey)}
              onClick={() => onDeclineAll(proposal, unavailableReason)}
            >
              {respondingActionKey === `decline-${proposal.id}`
                ? t('pbl.caseDetailDocuments.appointmentProposal.actions.submitting')
                : t('pbl.caseDetailDocuments.appointmentProposal.actions.noneWork')}
            </FooterTextButton>
          </FooterAction>
        </>
      )}
    </Panel>
  );
};

export const ContractNegotiationPanel = ({
  documents,
  canManageCase,
  canDownloadDocument,
  files,
  setFiles,
  onDragStateChange,
  onFilesDrop,
  fileNamePatternText,
  isUploading,
  signingDocumentId,
  loadingDocumentId,
  onInspectDocument,
  onDownloadDocument,
  onInviteToSign,
}: {
  documents: PblDocument[];
  canManageCase: boolean;
  canDownloadDocument: boolean;
  files: File[];
  setFiles: Dispatch<SetStateAction<File[]>>;
  onDragStateChange: (dragActive: boolean) => void;
  onFilesDrop: (files: File[]) => void;
  fileNamePatternText: string;
  isUploading: boolean;
  signingDocumentId: number | null;
  loadingDocumentId: number | null;
  onInspectDocument: AsyncDocumentHandler;
  onDownloadDocument: DocumentHandler;
  onInviteToSign: AsyncDocumentHandler;
}) => {
  //   @ts-ignore
  const { t } = useTranslation();
  const { isMobile } = useResize();
  const [mobileSearch, setMobileSearch] = useState('');
  const [mobilePage, setMobilePage] = useState(1);
  const [mobilePageSize, setMobilePageSize] = useState(10);
  const filteredMobileDocuments = useMemo(() => {
    const query = mobileSearch.trim().toLocaleLowerCase();

    if (!query) return documents;

    return documents.filter((document) =>
      [
        getDocumentFileName(document),
        document.title,
        getDocumentFileType(document),
        formatFileSize(document.storage_file?.size),
        formatDateTime(document.updated_at_display),
        getDocumentUploaderName(document),
      ]
        .filter(Boolean)
        .some((value) => String(value).toLocaleLowerCase().includes(query))
    );
  }, [documents, mobileSearch]);
  const mobilePageCount = Math.max(1, Math.ceil(filteredMobileDocuments.length / mobilePageSize));
  const mobilePageStart = (mobilePage - 1) * mobilePageSize;
  const mobileDocuments = filteredMobileDocuments.slice(mobilePageStart, mobilePageStart + mobilePageSize);
  const mobilePageNumbers = useMemo(() => {
    const start = Math.max(1, Math.min(mobilePage - 1, mobilePageCount - 2));
    const end = Math.min(mobilePageCount, start + 2);

    return Array.from({ length: end - start + 1 }, (_, index) => start + index);
  }, [mobilePage, mobilePageCount]);

  useEffect(() => {
    setMobilePage(1);
  }, [mobileSearch, mobilePageSize]);

  useEffect(() => {
    if (mobilePage > mobilePageCount) setMobilePage(mobilePageCount);
  }, [mobilePage, mobilePageCount]);
  const tableData = useMemo<DocumentTableRow[]>(
    () => documents.map((document) => ({ id: String(document.id), document })),
    [documents]
  );

  const columns = useMemo<ColumnDef<DocumentTableRow>[]>(
    () => [
      {
        accessorKey: 'name',
        header: t('pbl.caseDetailDocuments.documentsUpload.table.name'),
        meta: {
          headerClassName: 'pbl-document-name-column',
          headerStyle: { width: '40%', maxWidth: '40%' },
          cellClassName: 'pbl-document-name-column',
          cellStyle: { width: '40%', maxWidth: '40%' },
        },
        cell: ({ row }) => (
          <RenderDocumentName
            document={row.original.document}
            canInspectDocument={canManageCase}
            loadingDocumentId={loadingDocumentId}
            onInspectDocument={onInspectDocument}
          />
        ),
        enableSorting: false,
      },
      {
        accessorKey: 'type',
        header: t('pbl.caseDetailDocuments.documentsUpload.table.type'),
        cell: ({ row }) => <TypeText>{getDocumentFileType(row.original.document)}</TypeText>,
        enableSorting: false,
      },
      {
        accessorKey: 'size',
        header: t('pbl.caseDetailDocuments.documentsUpload.table.size'),
        cell: ({ row }) => formatFileSize(row.original.document.storage_file?.size),
        enableSorting: false,
      },
      {
        accessorKey: 'updated',
        header: t('pbl.caseDetailDocuments.documentsUpload.table.updated'),
        cell: ({ row }) => formatDateTime(row.original.document.updated_at_display),
        enableSorting: false,
      },
      {
        accessorKey: 'uploadedBy',
        header: t('pbl.caseDetailDocuments.documentsUpload.table.uploadedBy'),
        cell: ({ row }) => getDocumentUploaderName(row.original.document) || '-',
        enableSorting: false,
      },
      {
        accessorKey: 'action',
        header: t('pbl.caseDetailDocuments.documentsUpload.table.action'),
        cell: ({ row }) => {
          if (!canManageCase && !canDownloadDocument) return null;
          const document = row.original.document;

          return (
            <TableActions>
              {canDownloadDocument && (
                <Button variant="primary" type="button" onClick={() => onDownloadDocument(document)}>
                  {t('pbl.caseDetailDocuments.documentsUpload.actions.download')}
                </Button>
              )}
            </TableActions>
          );
        },
        enableSorting: false,
      },
    ],
    [
      canDownloadDocument,
      canManageCase,
      loadingDocumentId,
      onDownloadDocument,
      onInspectDocument,
      onInviteToSign,
      signingDocumentId,
      t,
    ]
  );

  return (
    <Panel>
      <SectionTitle>{t('pbl.caseDetailDocuments.documentsUpload.title')}</SectionTitle>
      <BulletList>
        <li>{t('pbl.caseDetailDocuments.documentsUpload.instructions.shareDocuments')}</li>
        <li>{t('pbl.caseDetailDocuments.documentsUpload.instructions.apiDocuments')}</li>
        <li>{t('pbl.caseDetailDocuments.documentsUpload.instructions.detailNeeded')}</li>
      </BulletList>
      {canManageCase && (
        <DocumentUploadArea>
          <DropZone
            files={files}
            buttonLabel={isMobile ? t('buttons.browse') : undefined}
            onDragStateChange={onDragStateChange}
            onFilesDrop={onFilesDrop}
            setFiles={setFiles}
          />
          <Text color={theme.colors.gray400} fontSize={theme.fontSizes.sm} textAlign="center">
            {isUploading ? t('pbl.caseDetailDocuments.documentsUpload.uploading') : fileNamePatternText}
          </Text>
        </DocumentUploadArea>
      )}
      {isMobile ? (
        <>
          <MobileSearchInput
            type="search"
            value={mobileSearch}
            placeholder={t('buttons.search')}
            aria-label={t('info.searchAllColumns')}
            onChange={(event) => setMobileSearch(event.target.value)}
          />
          {mobileDocuments.length > 0 ? (
            <MobileRecords>
              {mobileDocuments.map((document) => (
                <MobileRecord key={document.id}>
                  <MobileRecordLabel>{t('pbl.caseDetailDocuments.documentsUpload.table.name')}</MobileRecordLabel>
                  <MobileRecordValue>
                    <RenderDocumentName
                      document={document}
                      canInspectDocument={canManageCase}
                      loadingDocumentId={loadingDocumentId}
                      onInspectDocument={onInspectDocument}
                    />
                  </MobileRecordValue>
                  <MobileRecordLabel>{t('pbl.caseDetailDocuments.documentsUpload.table.type')}</MobileRecordLabel>
                  <MobileRecordValue><TypeText>{getDocumentFileType(document)}</TypeText></MobileRecordValue>
                  <MobileRecordLabel>{t('pbl.caseDetailDocuments.documentsUpload.table.size')}</MobileRecordLabel>
                  <MobileRecordValue>{formatFileSize(document.storage_file?.size)}</MobileRecordValue>
                  <MobileRecordLabel>{t('pbl.caseDetailDocuments.documentsUpload.table.updated')}</MobileRecordLabel>
                  <MobileRecordValue>{formatDateTime(document.updated_at_display)}</MobileRecordValue>
                  <MobileRecordLabel>{t('pbl.caseDetailDocuments.documentsUpload.table.uploadedBy')}</MobileRecordLabel>
                  <MobileRecordValue>{getDocumentUploaderName(document) || '-'}</MobileRecordValue>
                  <MobileRecordLabel>{t('pbl.caseDetailDocuments.documentsUpload.table.action')}</MobileRecordLabel>
                  <MobileRecordValue>
                    {canDownloadDocument ? (
                      <Button variant="primary" type="button" onClick={() => onDownloadDocument(document)}>
                        {t('pbl.caseDetailDocuments.documentsUpload.actions.download')}
                      </Button>
                    ) : (
                      '-'
                    )}
                  </MobileRecordValue>
                </MobileRecord>
              ))}
            </MobileRecords>
          ) : (
            <MobileEmptyRecord>{t('pbl.caseDetailDocuments.documentsUpload.table.empty')}</MobileEmptyRecord>
          )}
          <MobilePagination>
            <div>
              {t('pbl.caseDetailDocuments.documentsUpload.pagination.showing', {
                from: filteredMobileDocuments.length === 0 ? 0 : mobilePageStart + 1,
                to: Math.min(mobilePageStart + mobilePageSize, filteredMobileDocuments.length),
                total: filteredMobileDocuments.length,
              })}
            </div>
            <MobilePaginationDisplay>
              {t('info.display')}
              <select
                aria-label={t('info.display')}
                value={mobilePageSize}
                onChange={(event) => setMobilePageSize(Number(event.target.value))}
              >
                {[10, 20, 30, 40, 50].map((pageSize) => (
                  <option key={pageSize} value={pageSize}>{pageSize}</option>
                ))}
              </select>
            </MobilePaginationDisplay>
            <MobilePageButtons>
              <MobilePageButton
                type="button"
                disabled={mobilePage <= 1}
                aria-label={t('pbl.caseDetailDocuments.documentsUpload.pagination.previous')}
                onClick={() => setMobilePage((page) => Math.max(1, page - 1))}
              >
                ‹
              </MobilePageButton>
              {mobilePageNumbers.map((pageNumber) => (
                <MobilePageButton
                  key={pageNumber}
                  type="button"
                  $active={pageNumber === mobilePage}
                  aria-current={pageNumber === mobilePage ? 'page' : undefined}
                  onClick={() => setMobilePage(pageNumber)}
                >
                  {pageNumber}
                </MobilePageButton>
              ))}
              <MobilePageButton
                type="button"
                disabled={mobilePage >= mobilePageCount}
                aria-label={t('pbl.caseDetailDocuments.documentsUpload.pagination.next')}
                onClick={() => setMobilePage((page) => Math.min(mobilePageCount, page + 1))}
              >
                ›
              </MobilePageButton>
            </MobilePageButtons>
          </MobilePagination>
        </>
      ) : (
        <DocumentTableScroll>
          <DataTable columns={columns} data={tableData} canSearch />
        </DocumentTableScroll>
      )}
    </Panel>
  );
};

export const DocumentsToSignPanel = ({
  documents,
  item,
  assignedLawyerName,
  canManageCase,
}: {
  documents: PblDocument[];
  item: CaseProgressSummary;
  assignedLawyerName: string;
  canManageCase: boolean;
}) => {
  //   @ts-ignore
  const { t } = useTranslation();

  return (
  <Panel>
    <SectionTitle>{t('pbl.caseDetailDocuments.documentsToSign.title')}</SectionTitle>
    <BodyText>{t('pbl.caseDetailDocuments.documentsToSign.body')}</BodyText>
    {documents.length > 0 ? (
      documents.map((document) => (
        <SignGrid key={document.id}>
          <div>
            <SignColumnTitle>{t('pbl.caseDetailDocuments.documentsToSign.signFile')}</SignColumnTitle>
            <SignInfoGrid>
              <InfoLabel>{t('pbl.caseDetailDocuments.documentsToSign.contract')}</InfoLabel>
              <InfoLabel>{t('pbl.caseDetailDocuments.documentsToSign.uploadedBy')}</InfoLabel>
              <InfoValue>{getDocumentMetadataText(document, ['uploaded_by', 'uploaded_by_name']) || '-'}</InfoValue>
              <InfoLabel>{t('pbl.caseDetailDocuments.documentsToSign.uploadDate')}</InfoLabel>
              <InfoValue>{formatDateTime(document.created_at)}</InfoValue>
              <InfoValue>{getDocumentFileName(document)}</InfoValue>
            </SignInfoGrid>
          </div>
          <div>
            <SignColumnTitle>{t('pbl.caseDetailDocuments.documentsToSign.eSignatures')}</SignColumnTitle>
            <SignatureGrid>
              <SignatureBox>
                <InfoLabel>{t('pbl.caseDetailDocuments.documentsToSign.enquirer')}</InfoLabel>
                <InfoValue>{item.enquirer.fullName}</InfoValue>
                <InfoLabel>{t('pbl.caseDetailDocuments.documentsToSign.signedByEnquirer')}</InfoLabel>
                <SignedBadge>{t('pbl.caseDetailDocuments.documentsToSign.pending')}</SignedBadge>
                <InfoLabel>{t('pbl.caseDetailDocuments.documentsToSign.dateSigned')}</InfoLabel>
                <InfoValue>
                  {getDocumentMetadataText(document, ['enquirer_signed_at', 'citizen_signed_at']) || '-'}
                </InfoValue>
              </SignatureBox>
              <SignatureBox>
                <InfoLabel>{t('pbl.caseDetailDocuments.documentsToSign.lawyer')}</InfoLabel>
                <InfoValue>{assignedLawyerName}</InfoValue>
                <InfoLabel>{t('pbl.caseDetailDocuments.documentsToSign.signedByLawyer')}</InfoLabel>
                {canManageCase ? (
                  <SignButton type="button" disabled onClick={() => undefined}>
                    {t('pbl.caseDetailDocuments.documentsToSign.signDisabled')}
                  </SignButton>
                ) : (
                  <InfoValue>-</InfoValue>
                )}
                <InfoLabel>{t('pbl.caseDetailDocuments.documentsToSign.dateSigned')}</InfoLabel>
                <InfoValue>{getDocumentMetadataText(document, ['lawyer_signed_at']) || '-'}</InfoValue>
              </SignatureBox>
            </SignatureGrid>
          </div>
        </SignGrid>
      ))
    ) : (
      <BodyText>{t('pbl.caseDetailDocuments.documentsToSign.empty')}</BodyText>
    )}
  </Panel>
  );
};
